/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-width is an internal custom property from #utils/skeleton.css, not a design token */
import { html } from 'lit/static-html.js'
import { styleMap } from 'lit/directives/style-map.js'
import type { PropertyValues } from 'lit'
import { nothing } from 'lit'
import { property } from 'lit/decorators.js'

import { compose } from '#utils/mixins'
import { LfuiElement } from '#utils/element'
import skeletonStyles from '#utils/skeleton.css'
import { randomSkeletonPercentWidth, skeletonSrLabel } from '#utils/skeleton'

import styles from './lfds-list-row-content.css.ts'
const SLOT_NAMES = [
	'label-start',
	'sub-label-start',
	'label-end',
	'sub-label-end'
] as const
type SlotName = (typeof SLOT_NAMES)[number]
const isSlotName = (name: string): name is SlotName =>
	SLOT_NAMES.includes(name as SlotName)

// Sub-labels are secondary text and tend to run shorter than labels.
const LABEL_WIDTHS = [60, 70, 80, 90]
const SUB_LABEL_WIDTHS = [35, 45, 55]

/**
 * @name LfdsListRowContent
 *
 * @status ready
 * @tag lfds-list-row-content
 *
 * @slot label-start
 * @slot sub-label-start
 * @slot label-end
 * @slot sub-label-end
 */
export default class LfdsListRowContent extends compose(LfuiElement) {
	static styles = [LfuiElement.styles, skeletonStyles, styles]

	/**
	 * Controls the reading order of the slot content for screen readers.
	 *
	 * - `vertical` (default): reads column by column
	 *   → label-start, sub-label-start, label-end, sub-label-end
	 * - `horizontal`: reads row by row
	 *   → label-start, label-end, sub-label-start, sub-label-end
	 *
	 * Visual layout is unchanged — only DOM order (and thus screen reader
	 * announcement order) is affected.
	 */
	@property({ attribute: 'reading-order' })
	readingOrder: 'vertical' | 'horizontal' = 'vertical'

	/**
	 * Shows a shimmering placeholder over whichever `label`/`sub-label`
	 * slots are populated, in place of the real (still visible in DOM,
	 * visually masked) content. Automatically set by a parent
	 * `<lfds-list-row loading>` — set directly only when used standalone.
	 */
	@property({ type: Boolean, reflect: true }) loading: boolean

	/**
	 * By default, `loading` only shows a shimmer for slots that have no
	 * real content assigned yet — a slot with real content already present
	 * stays visible (e.g. a value simply being refreshed shouldn't
	 * disappear behind a shimmer). Set `force-shimmer` to also mask
	 * already-known content while `loading` (e.g. a recalculation where
	 * hiding the stale value until the new one arrives is preferred).
	 */
	@property({ type: Boolean, reflect: true, attribute: 'force-shimmer' })
	forceShimmer: boolean

	/**
	 * Overrides the generic loading screen-reader label. Pass a contextual
	 * string when this row's content is known (e.g. "Laddar bonsai-träd"), or
	 * `''` to suppress the label entirely when a wrapping region already
	 * announces loading for several grouped elements. Unset keeps the shared
	 * generic wording. Ignored when nested in a `loading` `<lfds-list-row>` —
	 * the row already owns the single announcement for the whole row.
	 */
	@property({ attribute: 'sr-loading-label' }) srLoadingLabel?: string

	#slotElements = new Map<SlotName, HTMLSlotElement>()
	#didInitSlotState = false
	#skeletonWidths: Record<SlotName, number> = {
		'label-start': randomSkeletonPercentWidth(LABEL_WIDTHS),
		'sub-label-start': randomSkeletonPercentWidth(SUB_LABEL_WIDTHS),
		'label-end': randomSkeletonPercentWidth(LABEL_WIDTHS),
		'sub-label-end': randomSkeletonPercentWidth(SUB_LABEL_WIDTHS)
	}

	#widthStyle(name: SlotName) {
		return styleMap({
			'--lfds-skeleton-width': `${this.#skeletonWidths[name]}%`
		})
	}

	connectedCallback() {
		super.connectedCallback()

		// Avoid mutating attributes in constructor. Some browser/runtime paths
		// (notably when instantiated via React wrappers) can throw
		// "NotSupportedError: Operation is not supported" for this.
		if (!this.#didInitSlotState) {
			for (const name of SLOT_NAMES) {
				this.#setSlotState(name, false)
			}
			this.#didInitSlotState = true
		}
	}

	protected firstUpdated(changedProperties: PropertyValues<this>) {
		super.firstUpdated(changedProperties)

		for (const name of SLOT_NAMES) {
			const slot = this.renderRoot.querySelector<HTMLSlotElement>(
				`slot[name='${name}']`
			)
			if (slot) {
				this.#slotElements.set(name, slot)
			}
		}

		this.#syncAllSlots()
	}

	updated(changed: PropertyValues<this>) {
		super.updated(changed)
		if (changed.has('loading')) {
			// A parent <lfds-list-row> already announces loading for the whole
			// row — setting aria-busy/a label here too would read out twice.
			if (this.closest('lfds-list-row')) {
				this.removeAttribute('aria-busy')
			} else {
				this.setAttribute('aria-busy', String(!!this.loading))
			}
		}
	}

	render() {
		const content =
			this.readingOrder === 'horizontal'
				? html`
						<div class="content">
							<div class="label-start" style=${this.#widthStyle('label-start')}>
								<slot
									name="label-start"
									@slotchange=${this.#handleSlotChange}
								></slot>
							</div>
							<div class="label-end" style=${this.#widthStyle('label-end')}>
								<slot
									name="label-end"
									@slotchange=${this.#handleSlotChange}
								></slot>
							</div>
							<div
								class="sub-label-start"
								style=${this.#widthStyle('sub-label-start')}
							>
								<slot
									name="sub-label-start"
									@slotchange=${this.#handleSlotChange}
								></slot>
							</div>
							<div
								class="sub-label-end"
								style=${this.#widthStyle('sub-label-end')}
							>
								<slot
									name="sub-label-end"
									@slotchange=${this.#handleSlotChange}
								></slot>
							</div>
						</div>
					`
				: html`
						<div class="content">
							<div class="label-start" style=${this.#widthStyle('label-start')}>
								<slot
									name="label-start"
									@slotchange=${this.#handleSlotChange}
								></slot>
							</div>
							<div
								class="sub-label-start"
								style=${this.#widthStyle('sub-label-start')}
							>
								<slot
									name="sub-label-start"
									@slotchange=${this.#handleSlotChange}
								></slot>
							</div>
							<div class="label-end" style=${this.#widthStyle('label-end')}>
								<slot
									name="label-end"
									@slotchange=${this.#handleSlotChange}
								></slot>
							</div>
							<div
								class="sub-label-end"
								style=${this.#widthStyle('sub-label-end')}
							>
								<slot
									name="sub-label-end"
									@slotchange=${this.#handleSlotChange}
								></slot>
							</div>
						</div>
					`

		const srLabel =
			this.loading && !this.closest('lfds-list-row')
				? skeletonSrLabel(this, this.srLoadingLabel)
				: ''

		return html`${content}${
			srLabel
				? html`<span class="hidden" role="status">${srLabel}</span>`
				: nothing
		}`
	}

	#handleSlotChange(event: Event) {
		if (!(event.target instanceof HTMLSlotElement)) {
			return
		}

		if (!isSlotName(event.target.name)) {
			return
		}

		this.#syncSlot(event.target.name, event.target)
	}

	#syncAllSlots() {
		for (const name of SLOT_NAMES) {
			this.#syncSlot(name)
		}
	}

	#syncSlot(name: SlotName, slot?: HTMLSlotElement | null) {
		const cached = this.#slotElements.get(name)
		const target = slot ?? cached

		if (!target) {
			return
		}

		if (!cached && slot) {
			this.#slotElements.set(name, slot)
		}

		this.#setSlotState(name, this.#slotHasContent(target))
	}

	#setSlotState(name: SlotName, hasContent: boolean) {
		const attr = `data-has-${name}`
		const value = hasContent ? 'true' : 'false'

		if (this.getAttribute(attr) !== value) {
			this.setAttribute(attr, value)
		}
	}

	#slotHasContent(slot: HTMLSlotElement) {
		return slot.assignedNodes({ flatten: true }).some((node) => {
			if (node.nodeType === Node.TEXT_NODE) {
				return (node.textContent ?? '').trim().length > 0
			}

			if (node.nodeType === Node.COMMENT_NODE) {
				return false
			}

			return true
		})
	}
}
